import {
  BottomSheet,
  Button,
  convertToKST,
  Modal,
  Styles,
  useGetCompanyInfo,
} from '@orcar/common-d2c';
import dayjs, { Dayjs } from 'dayjs';
import { type Dispatch, type SetStateAction, useState } from 'react';
import styled from 'styled-components';
import { Dropdown } from '@/components/dropdown';
import {
  getAvailablePickUpTimeOptions,
  getRentalOperatingHours,
  getTimeOptions,
} from '@/utils/date';
import { MobileDateSelection } from '../../pages/home/components/dateSelection/MobileDateSelection';

interface MobileOptionSearchModalProps {
  showModal: boolean;
  onClose: (option?: { onRouterBack?: boolean }) => void;
  pickUpDate: Dayjs;
  setPickUpDate: Dispatch<SetStateAction<Dayjs>>;
  dropOffDate: Dayjs;
  setDropOffDate: Dispatch<SetStateAction<Dayjs>>;
  pickUpTimeValue: number;
  setPickUpTimeValue: Dispatch<SetStateAction<number>>;
  dropOffTimeValue: number;
  setDropOffTimeValue: Dispatch<SetStateAction<number>>;
  handleNextBtn: () => void;
}

const MobileOptionSearchModal = ({
  showModal,
  onClose,
  pickUpDate,
  setPickUpDate,
  dropOffDate,
  setDropOffDate,
  pickUpTimeValue,
  setPickUpTimeValue,
  dropOffTimeValue,
  setDropOffTimeValue,
  handleNextBtn,
}: MobileOptionSearchModalProps) => {
  const [showTwoMonthWarning, setShowTwoMonthWarning] = useState(false);
  const todayInKST = dayjs().tz('Asia/seoul');
  const pickUpAtInKST = dayjs(convertToKST(pickUpDate?.format())).tz(
    'Asia/seoul',
  );
  const isToday = dayjs(pickUpAtInKST).isSame(todayInKST, 'date');
  const isEarlyDropOff = dropOffTimeValue <= 730;

  const { data: companyData } = useGetCompanyInfo();
  const rentalOperatingHours = getRentalOperatingHours(companyData);

  const pickUpTimeOptions = getTimeOptions('pickUp', rentalOperatingHours);
  const dropOffTimeOptions = getTimeOptions('dropOff', rentalOperatingHours);
  const availablePickUpTimeOptions =
    getAvailablePickUpTimeOptions(rentalOperatingHours);

  const pickUpOptions = isToday
    ? availablePickUpTimeOptions
    : pickUpTimeOptions;

  const dropOffOptions = pickUpDate?.isSame(dropOffDate, 'date')
    ? dropOffTimeOptions.filter((option) => option.value > pickUpTimeValue)
    : dropOffTimeOptions;

  // const handleNextBtnClick = () => {
  //   const currentDate = dayjs();
  //   const twoMonthsLater = currentDate.add(2, 'month');

  //   if (dropOffDate.isAfter(twoMonthsLater)) {
  //     setShowTwoMonthWarning(true);
  //     return;
  //   }
  //   handleNextBtn();
  // };

  return (
    <>
      {showModal && (
        <BottomSheet onClose={onClose}>
          <HeadWrapper>
            <Title>여행 일정</Title>
          </HeadWrapper>
          <SelectionWrapper>
            <MobileDateSelection
              pickUpDate={pickUpDate}
              setPickUpDate={setPickUpDate}
              dropOffDate={dropOffDate}
              setDropOffDate={setDropOffDate}
              isMobile
            />
          </SelectionWrapper>
          <ButtonWrapper>
            <TimeSelectionWrapper>
              <TimeSelection>
                <Time>대여 시간</Time>
                <Dropdown
                  width='100%'
                  direction='top'
                  options={pickUpOptions}
                  selectedValue={pickUpTimeValue}
                  onSelect={setPickUpTimeValue}
                />
              </TimeSelection>
              <TimeSelection>
                <Time>반납 시간</Time>
                <Dropdown
                  width='100%'
                  direction='top'
                  options={dropOffOptions}
                  selectedValue={dropOffTimeValue}
                  onSelect={setDropOffTimeValue}
                />
              </TimeSelection>
            </TimeSelectionWrapper>
            {isEarlyDropOff ? (
              <Text color={Styles.COLOR.DANGER}>
                선택하신 반납 시간은 새벽 반납 비용 1만원이 추가됩니다.
              </Text>
            ) : null}
            <Button
              height='60px'
              borderRadius='5px'
              font={Styles.FONT.H4}
              disabled={!pickUpDate || !dropOffDate}
              onClick={handleNextBtn}
            >
              적용하기
            </Button>
          </ButtonWrapper>
        </BottomSheet>
      )}
      {showTwoMonthWarning && (
        <Modal
          onClose={() => setShowTwoMonthWarning(false)}
          paddingHorizontal={33}
          paddingVertical={40}
          yPosition='450px'
          hasBorder={false}
          topLevel
        >
          <div className='flex flex-col items-center justify-center p-6'>
            <p className='mb-6 text-center text-xl font-semibold leading-relaxed'>
              2개월 이후 예약을 원하시면
              <br />
              <span className='text-primary'>제주로 렌터카</span>로 문의
              부탁드립니다
            </p>
            <p className='mb-6 text-gray-500'>TEL: 064-744-1116</p>
            <Button
              width='200px'
              height='45px'
              borderRadius='5px'
              font={{
                'font-size': '16px',
                'font-weight': '600',
                'line-height': '27px',
              }}
              onClick={() => setShowTwoMonthWarning(false)}
            >
              확인
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
};

export { MobileOptionSearchModal };

const HeadWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 20px;
  height: 76px;
  border-bottom: 1px solid ${Styles.COLOR.GREY_04};
`;

const Title = styled.p`
  font-size: 20px;
  font-weight: 600;
`;

const Text = styled.p<{ color?: string }>`
  position: absolute;
  top: 92px;
  font-size: 14px;
  color: ${({ color }) => color || Styles.COLOR.GREY_02};
`;

const SelectionWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  height: 100vh;
  padding-top: 35px;
  overflow-y: scroll;
  overflow-x: hidden;
`;

const ButtonWrapper = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 180px;
  padding: 16px 20px;
  background-color: ${Styles.COLOR.WHITE};
  box-shadow: 0px -2px 10px rgba(0, 0, 0, 0.1);
  z-index: 10;
`;

const TimeSelectionWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  gap: 8px;
`;

const TimeSelection = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 8px;
`;

const Time = styled.p`
  font-size: 14px;
  font-weight: 400;
  color: ${Styles.COLOR.GREY_02};
`;
